import { useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import {
  ArrowDownToLine,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleSlash,
  IterationCw,
  ListOrdered,
  XCircle,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/select';
import { useNodeStore } from '@/stores/node-store';
import type {
  CustomNode,
  InputValue,
  MapExecutionConfig,
  NodeStruct,
} from '@/types/node-types';
import type { NodeStatusEvent } from '@/types/workflow-types';
import { ConnectionType, type CustomEdge } from '@/types/edge-types';
import {
  attachMapBody,
  defaultMapConfig,
  detachMapBody,
  getMapConfig,
  isMapBodyEligible,
  validateMapConfig,
} from '@/pages/workflow/helper/map-control';
import {
  bodyInWords,
  failureModeInWords,
  mapItemRows,
  mapLimitsInWords,
  mapPanelTitle,
  mapProgressInWords,
} from '@/pages/workflow/helper/map-panel';
import { nodeLabel } from '@/pages/workflow/helper/repeat-loop';
import {
  describeForEach,
  isForEachEdge,
} from '@/pages/workflow/helper/for-each-region';
import { ForEachSentenceSection } from './for-each-sentence';
import { JsonEditorField } from './json-editor-field';
import { cn } from '@/lib/utils';

interface MapPanelProps {
  nodeId: string;
  disabled?: boolean;
  isExecuting?: boolean;
}

const hint = 'text-xs text-muted-foreground';

/**
 * The Map / For Each node as a form: what arrives, which step runs per
 * item, the limits in words, how failures are handled, live progress, and
 * after a run an inspector over every item in input order.
 */
export function MapPanel({
  nodeId,
  disabled = false,
  isExecuting = false,
}: MapPanelProps) {
  const { nodes, edges, groupedNodes, handleParameterChange } = useNodeStore(
    useShallow((state) => ({
      nodes: state.nodes,
      edges: state.edges,
      groupedNodes: state.groupedNodes,
      handleParameterChange: state.handleParameterChange,
    })),
  );
  const node = nodes.find((candidate: CustomNode) => candidate.id === nodeId);
  const struct: NodeStruct | undefined = node?.data?.struct;
  const catalog = useMemo(
    () => Object.values(groupedNodes).flat(),
    [groupedNodes],
  );
  const config: MapExecutionConfig | null = struct
    ? (getMapConfig(struct) ?? defaultMapConfig(nodeId, struct))
    : null;
  const eligibleBodies = useMemo(
    () => catalog.filter(isMapBodyEligible),
    [catalog],
  );
  const bodyStruct = config?.body
    ? catalog.find((entry) => entry.node_id === config.body?.catalog_node_id)
    : undefined;
  const problems = validateMapConfig(
    config,
    catalog.length ? catalog : undefined,
  );
  const sources = useMemo(
    () =>
      edges
        .filter(
          (edge: CustomEdge) =>
            edge.target === nodeId &&
            !isForEachEdge(edge) &&
            edge.edge_type !== ConnectionType.REPEAT &&
            edge.edge_type !== ConnectionType.CONDITIONAL,
        )
        .map((edge) => {
          const source = nodes.find(
            (candidate) => candidate.id === edge.source,
          );
          const port = source?.data?.struct?.output_ports?.find(
            (candidate) => candidate.id === edge.sourceHandle,
          );
          return {
            id: edge.id,
            name: nodeLabel(source),
            port: port?.display_name || port?.name || '',
          };
        }),
    [edges, nodes, nodeId],
  );
  const limits = mapLimitsInWords(struct, config);
  const progress = mapProgressInWords(
    node?.data?.result,
    node?.data?.nodeStatusEvents as NodeStatusEvent[] | undefined,
    isExecuting,
  );
  // --- for each (Phase 8c): a drawn body (item edge or collector) is the
  // multi-step mode; the single-step picker stays for every other Map. ---
  const forEach = useMemo(
    () => describeForEach(nodes, edges, nodeId),
    [nodes, edges, nodeId],
  );
  const regionMode = Boolean(forEach?.regionMode);
  const hasDrawnBody = Boolean(
    forEach && (forEach.itemEdges.length || forEach.collectEdges.length),
  );
  const [wantsRegion, setWantsRegion] = useState(false);
  const showRegion = hasDrawnBody || wantsRegion;
  const nodeNames = useMemo(
    () =>
      Object.fromEntries(nodes.map((entry) => [entry.id, nodeLabel(entry)])),
    [nodes],
  );
  const rows = useMemo(
    () =>
      mapItemRows(node?.data?.result, {
        nodeNames,
        order: forEach?.orderedIds,
      }),
    [node?.data?.result, nodeNames, forEach?.orderedIds],
  );
  // --- end for each ---
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [showJson, setShowJson] = useState(false);
  const [onlyFailed, setOnlyFailed] = useState(false);

  if (!struct || !config) return null;
  // The body rules for a drawn body are the For-each lint, not "choose a step".
  const shownProblems = showRegion
    ? validateMapConfig(
        { ...config, version: '1.1', body: null },
        catalog.length ? catalog : undefined,
      )
    : problems;

  const commit = (next: MapExecutionConfig) =>
    handleParameterChange(nodeId, 'map_config', next as unknown as InputValue);

  const visibleRows = onlyFailed
    ? rows.filter((row) => row.status === 'failed')
    : rows;

  return (
    <div className='space-y-4 px-3 py-2 text-sm' data-testid='map-panel'>
      <div
        className={cn(
          'flex items-center gap-1.5 text-sm font-semibold',
          regionMode && 'text-teal-800 dark:text-teal-200',
        )}
        data-testid='map-panel-title'
      >
        <IterationCw className='size-4' />
        {mapPanelTitle(regionMode)}
      </div>
      {/* What arrives */}
      <section className='space-y-1.5'>
        <Label className='text-sm'>What comes in</Label>
        {sources.length === 0 ? (
          <p className={hint} data-testid='map-sources-empty'>
            Nothing connected yet. Connect a list to the Items input; each entry
            becomes one run of the step.
          </p>
        ) : (
          <ol className='space-y-0.5 text-xs' data-testid='map-sources'>
            {sources.map((source) => (
              <li key={source.id} className='flex items-center gap-1.5'>
                <ArrowDownToLine className='size-3 text-muted-foreground' />
                <span className='font-medium'>{source.name}</span>
                {source.port ? (
                  <span className='text-muted-foreground'>· {source.port}</span>
                ) : null}
                <span className='text-muted-foreground'>(a list)</span>
              </li>
            ))}
          </ol>
        )}
      </section>

      {showRegion ? (
        <ForEachSentenceSection
          nodeId={nodeId}
          disabled={disabled}
          onCancel={() => setWantsRegion(false)}
        />
      ) : null}

      {/* The step */}
      <section className={cn('space-y-1.5', showRegion && 'hidden')}>
        <Label className='text-sm'>For each item, run</Label>
        <div className='flex flex-wrap items-center gap-2'>
          <Select
            options={eligibleBodies.map((entry) => entry.display_name)}
            selected={bodyStruct?.display_name}
            placeholder={
              eligibleBodies.length ? 'choose a step…' : 'no step available'
            }
            allowAllOption={false}
            disabled={disabled || eligibleBodies.length === 0}
            className='h-8 w-auto min-w-[160px] text-xs'
            onSelect={(label) => {
              const leaf = eligibleBodies.find(
                (entry) => entry.display_name === label,
              );
              if (!leaf) return;
              const attached = attachMapBody(detachMapBody(config), leaf);
              if (attached.ok) commit(attached.config);
            }}
          />
          {config.body ? (
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='h-8 text-xs'
              disabled={disabled}
              data-testid='map-body-remove'
              onClick={() => commit(detachMapBody(config))}
            >
              Remove
            </Button>
          ) : null}
        </div>
        <p className={hint} data-testid='map-body-words'>
          {bodyInWords(config, bodyStruct)}
        </p>
        {config.body && bodyStruct?.node_id === 'map_item_node' ? (
          <div className='flex items-center gap-2 text-xs'>
            <span>Return</span>
            <Select
              options={['each item', 'each item with its position']}
              selected={
                config.body.static_params?.projection === 'item_with_index'
                  ? 'each item with its position'
                  : 'each item'
              }
              allowAllOption={false}
              disabled={disabled}
              className='h-7 w-auto text-xs'
              onSelect={(value) =>
                commit({
                  ...config,
                  body: config.body
                    ? {
                        ...config.body,
                        static_params: {
                          ...config.body.static_params,
                          projection:
                            value === 'each item with its position'
                              ? 'item_with_index'
                              : 'item',
                        },
                      }
                    : null,
                })
              }
            />
          </div>
        ) : null}
        <button
          type='button'
          className={cn(
            hint,
            'flex items-center gap-1 underline-offset-2 hover:underline',
          )}
          disabled={disabled}
          data-testid='for-each-use-canvas'
          onClick={() => setWantsRegion(true)}
        >
          <IterationCw className='size-3' />
          Run several steps per item instead (use a part of the canvas)
        </button>
      </section>

      {/* Limits + failures */}
      <section className='space-y-1.5'>
        <Label className='text-sm'>Limits</Label>
        <p className='text-xs' data-testid='map-limits'>
          {limits.sentence}
        </p>
        <div className='flex flex-wrap items-center gap-2 text-xs'>
          <span>If an item fails:</span>
          <Select
            options={[
              'stop at the first failure',
              'run every item, report failures',
            ]}
            selected={
              config.failure_mode === 'all_settled'
                ? 'run every item, report failures'
                : 'stop at the first failure'
            }
            allowAllOption={false}
            disabled={disabled}
            className='h-7 w-auto text-xs'
            onSelect={(value) =>
              commit({
                ...config,
                failure_mode:
                  value === 'run every item, report failures'
                    ? 'all_settled'
                    : 'fail_fast',
              })
            }
          />
        </div>
        <p className={hint}>{failureModeInWords(config.failure_mode)}</p>
      </section>

      {shownProblems.length ? (
        <ul
          className='list-disc space-y-0.5 rounded-md border border-amber-300 dark:border-amber-500/40 bg-amber-50 p-2 pl-6 text-xs text-amber-900 dark:bg-amber-950/20 dark:text-amber-200'
          data-testid='map-problems'
        >
          {shownProblems.map((problem) => (
            <li key={problem}>{problem}</li>
          ))}
        </ul>
      ) : null}

      {/* Progress / result */}
      {progress ? (
        <section className='space-y-2' data-testid='map-progress'>
          <div className='flex items-center justify-between'>
            <Label className='text-sm'>
              {progress.state === 'running' ? 'Running' : 'Last run'}
            </Label>
            <span className='text-xs font-medium'>{progress.sentence}</span>
          </div>
          <div className='h-2 w-full overflow-hidden rounded bg-muted'>
            <div
              className={cn(
                'h-full transition-all',
                progress.failed ? 'bg-amber-500' : 'bg-emerald-500',
              )}
              style={{ width: `${Math.round(progress.fraction * 100)}%` }}
            />
          </div>
          {rows.length ? (
            <div className='space-y-1'>
              <div className='flex items-center justify-between text-xs'>
                <span className='flex items-center gap-1 text-muted-foreground'>
                  <ListOrdered className='size-3.5' />
                  Items, in input order
                </span>
                {progress.failed ? (
                  <button
                    type='button'
                    className='text-xs underline-offset-2 hover:underline'
                    onClick={() => setOnlyFailed((value) => !value)}
                  >
                    {onlyFailed ? 'show all' : 'only failed'}
                  </button>
                ) : null}
              </div>
              <ol
                className='max-h-64 space-y-0.5 overflow-y-auto rounded border text-xs'
                data-testid='map-items'
              >
                {visibleRows.map((row) => {
                  const open = openIndex === row.index;
                  return (
                    <li
                      key={row.index}
                      className='border-b last:border-b-0'
                      data-testid={`map-item-${row.index}`}
                    >
                      <button
                        type='button'
                        className='flex w-full items-center gap-2 px-2 py-1 text-left hover:bg-muted/40'
                        onClick={() => setOpenIndex(open ? null : row.index)}
                      >
                        {open ? (
                          <ChevronDown className='size-3 shrink-0' />
                        ) : (
                          <ChevronRight className='size-3 shrink-0' />
                        )}
                        <span className='w-8 shrink-0 font-mono text-muted-foreground'>
                          #{row.index + 1}
                        </span>
                        {row.status === 'completed' ? (
                          <CheckCircle2 className='size-3.5 shrink-0 text-emerald-600' />
                        ) : (
                          <XCircle className='size-3.5 shrink-0 text-red-500' />
                        )}
                        <span className='min-w-0 truncate font-mono'>
                          {row.status === 'completed'
                            ? row.preview
                            : row.errorMessage}
                        </span>
                      </button>
                      {open && row.steps.length ? (
                        <ol
                          className='space-y-0.5 bg-muted/20 px-3 py-1.5'
                          data-testid={`map-item-${row.index}-steps`}
                        >
                          {row.steps.map((step) => (
                            <li
                              key={step.nodeId}
                              className='flex items-center gap-1.5 text-[11px]'
                              data-testid={`map-item-${row.index}-step-${step.nodeId}`}
                              data-status={step.status}
                            >
                              {step.status === 'completed' ? (
                                <CheckCircle2 className='size-3 shrink-0 text-emerald-600 dark:text-emerald-400' />
                              ) : step.status === 'skipped' ? (
                                <CircleSlash className='size-3 shrink-0 text-muted-foreground' />
                              ) : (
                                <XCircle className='size-3 shrink-0 text-red-500 dark:text-red-400' />
                              )}
                              <span className='shrink-0 font-medium'>
                                {step.name}
                              </span>
                              <span
                                className={cn(
                                  'min-w-0 truncate',
                                  step.status === 'skipped'
                                    ? 'italic text-muted-foreground'
                                    : 'font-mono',
                                )}
                              >
                                {step.skipSentence ?? step.preview}
                              </span>
                            </li>
                          ))}
                        </ol>
                      ) : null}
                      {open ? (
                        <pre className='max-h-40 overflow-auto bg-muted/30 px-3 py-2 font-mono text-[11px]'>
                          {row.status === 'completed'
                            ? JSON.stringify(row.value, null, 2)
                            : `${row.errorMessage}\n(${row.errorCode})`}
                        </pre>
                      ) : null}
                    </li>
                  );
                })}
              </ol>
            </div>
          ) : null}
        </section>
      ) : null}

      {/* Escape hatch */}
      <div>
        <button
          type='button'
          className={cn(hint, 'underline-offset-2 hover:underline')}
          data-testid='map-json-toggle'
          onClick={() => setShowJson((value) => !value)}
        >
          {showJson ? 'Hide JSON' : 'Edit as JSON'}
        </button>
        {showJson ? (
          <div className='mt-1'>
            <JsonEditorField
              value={config}
              disabled={disabled}
              expectedShape='object'
              aria-label='Map configuration as JSON'
              onCommit={(value) => commit(value as MapExecutionConfig)}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
